import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  SOCKET_URL,
  initializeSocket,
  updateSocketToken,
  formatBearerToken,
} from '../config/socket';
import { AuthContext } from './AuthContext';

const SocketContext = createContext(null);

/**
 * هوک داخلی کانتکست سوکت
 */
export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (!context) {
    return {
      socket: null,
      isConnected: false,
      socketId: null,
      error: null,
      connect: () => {},
      disconnect: () => {},
      emit: () => {},
      on: () => () => {},
      off: () => {},
    };
  }
  return context;
};

/**
 * پرووایدر سراسری مدیریت اتصال Socket.io
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.url] آدرس سرور سوکت (پیش‌فرض: http://localhost:4000)
 * @param {Object} [props.options] گزینه‌های اتصال Socket.io
 * @param {boolean} [props.autoConnect=true] اتصال خودکار به محض لود برنامه
 * @param {string} [props.token] توکن احراز هویت سفارشی
 * @param {boolean} [props.requireAuth=true] آیا اتصال سوکت نیازمند احراز هویت است
 */
export const SocketProvider = ({
  children,
  url = SOCKET_URL,
  options = {},
  autoConnect = true,
  token,
  requireAuth = true,
}) => {
  const authContext = useContext(AuthContext);
  const activeToken = token || authContext?.token || localStorage.getItem('authToken');

  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState(null);
  const [error, setError] = useState(null);

  // نگهداری سوکت در ref برای پایداری در تمام رندرها
  const socketRef = useRef(null);
  if (!socketRef.current) {
    const bearerToken = formatBearerToken(activeToken);
    const { extraHeaders: customHeaders, auth: customAuth, ...restOptions } = options;
    socketRef.current = initializeSocket(url, {
      ...restOptions,
      extraHeaders: {
        ...(customHeaders || {}),
        ...(bearerToken ? { authorization: bearerToken } : {}),
      },
      auth: {
        ...(customAuth || {}),
        ...(bearerToken ? { authorization: bearerToken, token: bearerToken } : {}),
      },
    });
  }
  const socket = socketRef.current;

  // نگهداری مقادیر به‌روز در ref برای لیسنرهای رویداد
  const activeTokenRef = useRef(activeToken);
  useEffect(() => {
    activeTokenRef.current = activeToken;
  }, [activeToken]);

  const requireAuthRef = useRef(requireAuth);
  useEffect(() => {
    requireAuthRef.current = requireAuth;
  }, [requireAuth]);

  // مدیریت رویدادهای اتصال سوکت
  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => {
      setIsConnected(true);
      setSocketId(socket.id);
      setError(null);
    };

    const handleDisconnect = (reason) => {
      setIsConnected(false);
      setSocketId(null);
      if (reason === 'io server disconnect') {
        // قطع ارتباط از سمت سرور؛ تلاش مجدد فقط در صورت داشتن توکن یا عدم الزام احراز هویت
        if (!requireAuthRef.current || activeTokenRef.current) {
          socket.connect();
        }
      }
    };

    const handleConnectError = (err) => {
      setIsConnected(false);
      setError(err);
      if (!requireAuthRef.current || activeTokenRef.current) {
        console.warn('⚠️ [Socket.io] Connection error:', err.message || err);
      }
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);

    // بررسی وضعیت فعلی
    if (socket.connected) {
      setIsConnected(true);
      setSocketId(socket.id);
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
    };
  }, [socket]);

  // نگهداری توکن قبلی برای تشخیص لاگین/لاگ‌اوت و تغییر توکن
  const prevTokenRef = useRef(null);

  // همگام‌سازی توکن با سوکت و مدیریت اتصال خودکار پس از لاگین/لاگ‌اوت
  useEffect(() => {
    if (!socket) return;

    const bearerToken = formatBearerToken(activeToken);

    // به‌روزرسانی هدر و شیء احراز هویت سوکت
    if (bearerToken) {
      if (socket.io?.opts) {
        socket.io.opts.extraHeaders = {
          ...(socket.io.opts.extraHeaders || {}),
          authorization: bearerToken,
        };
      }
      socket.auth = {
        ...(socket.auth || {}),
        token: bearerToken,
        authorization: bearerToken,
      };
      updateSocketToken(activeToken);
    } else {
      if (socket.io?.opts?.extraHeaders) {
        delete socket.io.opts.extraHeaders.authorization;
      }
      if (socket.auth) {
        delete socket.auth.token;
        delete socket.auth.authorization;
      }
      updateSocketToken(null);
    }

    const tokenChanged = prevTokenRef.current !== activeToken;
    prevTokenRef.current = activeToken;

    // اگر احراز هویت اجباری است ولی کاربر لاگین نیست، اتصال برقرار نشود یا قطع گردد
    if (requireAuth && !activeToken) {
      if (socket.connected || socket.active) {
        socket.disconnect();
      }
      setIsConnected(false);
      setSocketId(null);
      setError(null);
      return;
    }

    // در صورتی که اتصال خودکار فعال است:
    if (autoConnect) {
      if (tokenChanged) {
        // توکن پس از لاگین یا تعویض شرکت تغییر کرده است؛
        // برای اعمال اعتبارسنجی جدید سوکت را ریست کرده و متصل می‌شویم
        socket.disconnect().connect();
      } else if (!socket.connected && !socket.active) {
        socket.connect();
      }
    }
  }, [socket, activeToken, autoConnect, requireAuth]);

  // توابع کمکی
  const connect = useCallback(() => {
    if (!socket) return;
    if (requireAuth && !activeToken) {
      console.warn('⚠️ [Socket.io] Cannot connect: Authentication token is required.');
      return;
    }
    if (!socket.connected) {
      socket.connect();
    }
  }, [socket, requireAuth, activeToken]);

  const disconnect = useCallback(() => {
    if (socket && (socket.connected || socket.active)) {
      socket.disconnect();
    }
  }, [socket]);

  const emit = useCallback(
    (eventName, ...args) => {
      if (socket) {
        socket.emit(eventName, ...args);
      }
    },
    [socket]
  );

  const on = useCallback(
    (eventName, callback) => {
      if (!socket) return () => {};
      socket.on(eventName, callback);
      return () => {
        socket.off(eventName, callback);
      };
    },
    [socket]
  );

  const off = useCallback(
    (eventName, callback) => {
      if (socket) {
        socket.off(eventName, callback);
      }
    },
    [socket]
  );

  const contextValue = useMemo(
    () => ({
      socket,
      isConnected,
      socketId,
      error,
      connect,
      disconnect,
      emit,
      on,
      off,
    }),
    [socket, isConnected, socketId, error, connect, disconnect, emit, on, off]
  );

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
